import { Box, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { iconSizeByButtonSize } from '@/components/form/baseInputs/Button/styles';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormConfigurationProvider } from '@/components/form/context/FormConfigurationContext';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Download01Icon } from '@/components/icons/Download01Icon';
import { PopperContent } from '@/components/poppers/PopperContent';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { MiniTable } from '@/components/tables/MiniTable/MiniTable';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { DocumentAssociatedIndividualPicker } from '@/modules/documents/DocumentDetailsForm/DocumentAssociatedIndividualPicker';
import { formatCreatedFromEntities } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { useDocumentDownload } from '@/modules/files/hooks/useFileDownload';
import { EntityStage } from '@/types/schema';
import { PathsOf } from '@/types/subform';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';
import { truncateToLength } from '@/utils/stringUtils';

import {
  getEntitiesFromRelationships,
  getTestamentaryEntitiesFromRelationships,
} from '../documentRelationships.utils';
import {
  ADDITIONAL_DOCUMENTS_POPPER_CONTENT,
  DOCUMENT_TYPE_OPTIONS,
} from '../documents.constants';
import {
  documentTypeIsRestricted,
  isClientProfileDocumentType,
} from '../documents.utils';
import { DocumentDetails_DocumentFragment } from '../graphql/DocumentDetails.generated';
import { DocumentDetailsFormShape } from './DocumentDetailsForm.types';
import { useSaveDocumentDetailsForm } from './hooks/useSaveDocumentDetailsForm';

type DocumentDetailsFormShapeProps = PathsOf<DocumentDetailsFormShape>;

function DocumentDetailsFormInner({
  document,
  showEntity,
}: DocumentDetailsFormProps) {
  const { control } = useFormContext<DocumentDetailsFormShape>();

  const [selectedDocumentKind] = useWatch({
    control,
    name: ['documentKind'],
  });

  const entities = getNodes(document.household.entities);
  const testamentaryEntities = getNodes(
    document.household.testamentaryEntities
  );

  const entityOptions: TypeaheadSelectInputOption<string>[] = useMemo(() => {
    return entities
      .filter((entity) => entity.stage !== EntityStage.PreCreated)
      .map((entity) => {
        return {
          value: entity.id,
          display: entity.subtype.displayName,
          groupName: 'Entities',
        };
      });
  }, [entities]);

  const testamentaryEntityOptions: TypeaheadSelectInputOption<string>[] =
    useMemo(
      () =>
        testamentaryEntities.map((te) => {
          return {
            value: te.id,
            display: te.displayName,
            groupName: 'Sub-trusts',
            secondaryText:
              truncateToLength(
                formatCreatedFromEntities(te.createdFromEntities) ?? '',
                70
              ) ?? undefined,
          };
        }),
      [testamentaryEntities]
    );

  const associatedEntityOptions = useMemo(() => {
    return [...entityOptions, ...testamentaryEntityOptions];
  }, [entityOptions, testamentaryEntityOptions]);

  const isClientProfileDocument =
    isClientProfileDocumentType(selectedDocumentKind);
  const [associatedEntitiesInputValue, setAssociatedEntitiesInputValue] =
    useState('');

  const primaryPicker = useMemo(() => {
    if (isClientProfileDocument) {
      return (
        <DocumentAssociatedIndividualPicker
          control={control}
          selectedDocumentKind={selectedDocumentKind}
        />
      );
    }

    return null;
  }, [control, isClientProfileDocument, selectedDocumentKind]);

  return (
    <>
      {primaryPicker}
      <Box sx={{ maxWidth: '100%' }}>
        <FormAwareTypeaheadMultiSelectInput<DocumentDetailsFormShape>
          options={associatedEntityOptions}
          placeholder="Search entities and sub-trusts"
          inputValue={associatedEntitiesInputValue}
          onInputChange={setAssociatedEntitiesInputValue}
          disabled={documentTypeIsRestricted(selectedDocumentKind)}
          control={control}
          label="Associated entities"
          fieldName={
            'associatedEntityIds' as const satisfies DocumentDetailsFormShapeProps
          }
          contextualHelp={
            <PopperContent body={ADDITIONAL_DOCUMENTS_POPPER_CONTENT} />
          }
        />
      </Box>
      {!showEntity && (
        <FormAwareSwitch<DocumentDetailsFormShape>
          control={control}
          label="Display as default when editing entity"
          fieldName={
            'isDefaultForEntity' as const satisfies DocumentDetailsFormShapeProps
          }
          labelPosition="right"
        />
      )}
      <FormAwareTextInput<DocumentDetailsFormShape>
        control={control}
        label="File name"
        required
        fieldName={'fileName' as const satisfies DocumentDetailsFormShapeProps}
      />
      <FormAwareTypeaheadSelectInput<DocumentDetailsFormShape>
        options={DOCUMENT_TYPE_OPTIONS}
        disabled={documentTypeIsRestricted(selectedDocumentKind)}
        control={control}
        placeholder="Search document types..."
        label="Type"
        required
        fieldName={
          'documentKind' as const satisfies DocumentDetailsFormShapeProps
        }
      />
    </>
  );
}

interface DocumentDetailsFormProps {
  document: DocumentDetails_DocumentFragment;
  showEntity: boolean;
}

export function DocumentDetailsForm({
  document,
  showEntity,
}: DocumentDetailsFormProps) {
  const [downloadFile, { loading: downloadingDocument }] =
    useDocumentDownload();
  const canEdit = useCanEditHouseholdData();

  const associatedEntityIds = useMemo(() => {
    const relationships = getNodes(document.entityDocumentRelationships);
    const entityIds = getEntitiesFromRelationships(relationships).map(
      (entity) => entity.id
    );
    const testamentaryEntityIds = getTestamentaryEntitiesFromRelationships(
      relationships
    ).map((entity) => entity.id);

    return Array.from(new Set([...entityIds, ...testamentaryEntityIds]));
  }, [document]);

  const formMethods = useForm<DocumentDetailsFormShape>({
    defaultValues: {
      _documentId: document.id,
      fileName: document.name,
      documentKind: document.type,
      isDefaultForEntity: !!document?.defaultDocumentOfEntity?.id,
      associatedEntityIds,
      associatedClientProfileId: document?.clientProfile?.id || '',
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isSubmitSuccessful },
    reset,
    getValues,
  } = formMethods;

  // Reset form defaultValues after a successful save so that isDirty
  // tracks against the newly-saved state rather than the stale initial mount values.
  // Without this, toggling a value back to its original (pre-save) state would
  // incorrectly show isDirty as false even though the server state differs.
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues());
    }
  }, [isSubmitSuccessful, reset, getValues]);

  const { updateDocument, loading, validateForm } =
    useSaveDocumentDetailsForm(document);

  const handleValidSubmit: SubmitHandler<DocumentDetailsFormShape> = (form) =>
    updateDocument(validateForm(form));

  const onSubmit = handleSubmit(handleValidSubmit);

  const rows = useMemo(() => {
    const valueProps = {
      textAlign: 'right',
    } as const;

    return [
      {
        label: 'Uploaded date',
        value: formatDateToMonDDYYYY(document.createdAt),
        valueProps,
      },
      {
        label: 'Created by',
        value: document.user.displayName,
        valueProps,
      },
    ];
  }, [document.createdAt, document.user.displayName]);

  return (
    <FormConfigurationProvider
      value={{ optionalDisplayType: 'required-asterisk' }}
    >
      <HouseholdPermissionAwareDisabledProvider forceDisabled={loading}>
        <FormProvider {...formMethods}>
          <Stack spacing={2} component="form" onSubmit={onSubmit}>
            <DocumentDetailsFormInner
              document={document}
              showEntity={showEntity}
            />
          </Stack>
        </FormProvider>
      </HouseholdPermissionAwareDisabledProvider>
      <MiniTable variant="default" rows={rows} />
      <Stack direction="row" justifyContent="space-between">
        {canEdit && (
          <Button
            size="sm"
            variant="primary"
            onClick={onSubmit}
            loading={loading}
            disabled={!isDirty}
          >
            Save changes
          </Button>
        )}
        <Tooltip title="Download document">
          <Button
            size="sm"
            square
            variant="secondary"
            loading={downloadingDocument}
            onClick={() => downloadFile(document.id)}
          >
            <Download01Icon size={iconSizeByButtonSize.sm} />
          </Button>
        </Tooltip>
      </Stack>
    </FormConfigurationProvider>
  );
}
